
import React, { useState, useEffect } from 'react';
import { Product, Category } from '../types';
import { Tag, Plus, Search, Filter, Check, Camera, Cloud, CloudOff, Database, WifiOff } from 'lucide-react';
import Button from './Button';
import ProductFormModal from './modals/ProductFormModal';
import ProductDetailModal from './modals/ProductDetailModal';
import { isSupabaseConfigured } from '../services/supabaseClient';

interface CatalogProps {
  products: Product[];
  categories: Category[];
  onAddProduct: (product: Product) => void;
  onUpdateProduct: (product: Product) => void;
  onDeleteProduct: (productId: string) => void;
  onAddCategory: (name: string) => void;
  onNavigateToStudio: (product: Product) => void;
}

const Catalog: React.FC<CatalogProps> = ({ 
  products, 
  categories, 
  onAddProduct, 
  onDeleteProduct, 
  onAddCategory,
  onNavigateToStudio 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [isCloudActive, setIsCloudActive] = useState(false);

  useEffect(() => {
    setIsCloudActive(isSupabaseConfigured);
  }, []);

  const allTags = Array.from(new Set(products.flatMap(p => p.tags)));
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          product.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategories.length === 0 || 
                            product.categoryIds.some(id => selectedCategories.includes(id));
    const matchesTags = selectedTags.length === 0 || 
                        selectedTags.every(tag => product.tags.includes(tag));
    return matchesSearch && matchesCategory && matchesTags;
  });

  const toggleCategory = (catId: string) => {
    setSelectedCategories(prev => 
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-6">
        <div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-3 tracking-tight">Product Catalog</h2>
          <div className="flex items-center gap-3">
            <p className="text-lg text-stone-500 font-light">Manage your digital inventory and assets.</p>
            {/* Connection Status Badge */}
            {isCloudActive ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold uppercase tracking-wider">
                    <Cloud className="w-3 h-3" /> Cloud Synced
                </div>
            ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-100 border border-stone-200 text-stone-500 text-[10px] font-bold uppercase tracking-wider" title="Supabase keys missing. Saving to browser storage only.">
                    <Database className="w-3 h-3" /> Local Storage
                </div>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowCategoryManager(!showCategoryManager)} className="bg-white hover:bg-stone-50 border-stone-200">
            Manage Categories
          </Button>
          <Button onClick={() => setShowAddProduct(true)} className="shadow-xl shadow-stone-900/20 px-6">
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {showCategoryManager && (
        <div className="mb-8 p-6 bg-white rounded-3xl border border-stone-100 shadow-xl shadow-stone-200/50 animate-in fade-in slide-in-from-top-4">
          <h3 className="font-bold text-stone-900 mb-4 text-sm uppercase tracking-wider">Manage Categories</h3>
          <div className="flex gap-3 mb-6 max-w-md">
            <input 
              type="text" 
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="New Category Name"
              className="flex-1 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-900 focus:border-transparent transition-shadow text-sm"
            />
            <Button onClick={() => { 
              if (newCategoryName.trim()) {
                onAddCategory(newCategoryName.trim());
                setNewCategoryName('');
              }
            }} disabled={!newCategoryName.trim()}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <span key={cat.id} className="bg-stone-50 border border-stone-200 text-stone-600 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide">
                {cat.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-12">
        {/* Sidebar Filters */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-10">
          {/* Search */}
          <div className="relative group">
            <Search className="absolute left-4 top-3.5 w-4 h-4 text-stone-400 group-focus-within:text-stone-900 transition-colors" />
            <input 
              type="text" 
              placeholder="Search inventory..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-2xl focus:ring-2 focus:ring-stone-900 focus:outline-none transition-shadow shadow-sm text-sm font-medium"
            />
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-widest text-stone-400 mb-5 flex items-center">
              <Filter className="w-3 h-3 mr-2" /> Categories
            </h4>
            <div className="space-y-3">
              {categories.map(cat => (
                <label key={cat.id} className="flex items-center space-x-3 cursor-pointer group">
                  <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-200 ${selectedCategories.includes(cat.id) ? 'bg-stone-900 border-stone-900 shadow-sm' : 'border-stone-300 bg-white group-hover:border-stone-400'}`}>
                      {selectedCategories.includes(cat.id) && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                  <input 
                    type="checkbox" 
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                    className="hidden" 
                  />
                  <span className={`text-sm transition-colors ${selectedCategories.includes(cat.id) ? 'text-stone-900 font-bold' : 'text-stone-500 font-medium group-hover:text-stone-700'}`}>{cat.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tags */}
          {allTags.length > 0 && (
            <div>
              <h4 className="font-bold text-xs uppercase tracking-widest text-stone-400 mb-5 flex items-center">
                <Tag className="w-3 h-3 mr-2" /> Popular Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button 
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 font-medium ${
                      selectedTags.includes(tag) 
                        ? 'bg-stone-900 text-white border-stone-900 shadow-md' 
                        : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-700'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Product Grid */}
        <div className="flex-1">
          {filteredProducts.length === 0 ? (
             <div className="text-center py-32 bg-white rounded-[2rem] border border-stone-100 shadow-sm border-dashed">
                <div className="w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-8 h-8 text-stone-300" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">No products found</h3>
                <p className="text-stone-400 mb-8 max-w-xs mx-auto">Try adjusting your search criteria or add a new product to your catalog.</p>
                <Button variant="outline" onClick={() => {
                  setSearchTerm('');
                  setSelectedCategories([]);
                  setSelectedTags([]);
                }}>
                  Clear Filters
                </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map(product => (
                <div 
                  key={product.id} 
                  className="group bg-white rounded-3xl p-3 shadow-sm hover:shadow-2xl hover:shadow-stone-200/50 hover:-translate-y-2 transition-all duration-500 border border-stone-100 cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="aspect-[3/4] bg-[#F5F5F4] rounded-2xl relative overflow-hidden mb-4">
                    {product.images.length > 0 ? (
                      <img 
                        src={product.images[0].url} 
                        alt={product.name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <Camera className="w-12 h-12" />
                      </div>
                    )}
                    
                    {/* Badge */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] font-bold text-stone-900 shadow-sm border border-white/20">
                      {product.images.length} {product.images.length === 1 ? 'Asset' : 'Assets'}
                    </div>

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Hover Action */}
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 w-[90%] translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-75">
                        <Button 
                            size="sm" 
                            variant="glass"
                            className="w-full justify-center bg-white text-stone-900 hover:bg-white shadow-lg border-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNavigateToStudio(product);
                            }}
                        >
                            Open Studio
                        </Button>
                    </div>
                  </div>
                  
                  <div className="px-2 pb-2">
                    <div className="flex justify-between items-start mb-1">
                         <h3 className="font-serif font-bold text-lg text-stone-900 truncate pr-2">{product.name}</h3>
                    </div>
                    
                    <div className="flex flex-wrap gap-1.5 mb-2 h-6 overflow-hidden">
                      {product.categoryIds.slice(0, 2).map(catId => {
                         const cat = categories.find(c => c.id === catId);
                         return cat ? (
                           <span key={catId} className="text-[10px] bg-stone-100 text-stone-500 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">
                             {cat.name}
                           </span>
                         ) : null;
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddProduct && (
        <ProductFormModal 
          categories={categories}
          onClose={() => setShowAddProduct(false)}
          onSubmit={onAddProduct}
        />
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          categories={categories}
          onClose={() => setSelectedProduct(null)}
          onNavigateToStudio={() => {
            onNavigateToStudio(selectedProduct);
            setSelectedProduct(null);
          }}
          onDelete={() => {
            if(confirm('Are you sure you want to delete this product?')) {
               onDeleteProduct(selectedProduct.id);
               setSelectedProduct(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default Catalog;
